import React, { useEffect, useMemo } from "react";
import { useFormikContext, FieldArray } from "formik";
import get from "lodash/get";
import classnames from "classnames";
import DebounceInput from "components/DebounceInput";
import HoverIconButton from "components/HoverIconButton";
import Callout from "components/Callout";
import cloneDeep from "lodash/cloneDeep";
import Select from "components/Form/fields/Select";
import {
  OPERATOR_FIXED_VALUE,
  OPERATOR_OPTIONS
} from "constants/request/validation";

const KeyOperatorValueRow = ({ pair, keyIx, name }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const pairs = get(values, name);
  const {
    uuid,
    key,
    value,
    error: errorValue,
    keyDisabled,
    preventDelete
  } = pair;
  const pairKey = `${name}[${keyIx}]`;
  const pairKeyPath = `${pairKey}.key`;
  const pairOperatorPath = `${pairKey}.operator`;
  const pairValuePath = `${pairKey}.value`;
  const pairErrorPath = `${pairKey}.error`;

  const operatorValue = get(values, pairOperatorPath);
  const pairValue = get(values, pairValuePath);

  const disableValue = useMemo(() => {
    return OPERATOR_FIXED_VALUE[operatorValue];
  }, [operatorValue]);
  useEffect(() => {
    if (pairValue && disableValue) {
      setTimeout(() => setFieldValue(pairValuePath, ""), 0);
    }
  }, [pairValue, disableValue, setFieldValue]);

  const fieldTouched = get(touched, pairKey);

  const selectProps = {
    options: OPERATOR_OPTIONS,
    stateKey: pairOperatorPath,
    value: operatorValue,
    onChange: (key) => (evt) => {
      const selection = evt.currentTarget.value;

      setFieldValue(key, selection);
      if (!get(touched, pairOperatorPath)) {
        setFieldTouched(key, true);
      }
    },
    labelTransform: (val) => {
      return val;
    },
    customClasses: {
      container: "h-100",
      input:
        "border-box input--common input-reset ba b--black-20 pa2 mb0 db w-100 pv2 f6 h-100"
    }
  };

  return (
    <li
      key={uuid}
      className={classnames(
        "flex flex-column w-100 pb3 pb0-ns bb bn-ns hairline-1",
        {
          pt3: keyIx
        }
      )}
    >
      <div className="w-100 pb0">
        <DebounceInput
          disabled={keyDisabled}
          inputValue={key}
          placeholder="Key"
          debounce={500}
          name={pairKeyPath}
          customClasses={{
            input: `input--common brtl-6 brtr-6`
          }}
          valueSet={(val) => {
            setFieldValue(pairKeyPath, val);

            if (!fieldTouched) {
              setFieldTouched(pairKey, true);
            }
          }}
        />
      </div>
      <div className="w-100 pb0">
        <Select {...selectProps} />
      </div>
      <div className="flex flex-row w-100">
        <DebounceInput
          inputValue={value}
          placeholder={!disableValue ? "Value" : ""}
          customClasses={{
            input: `input--common brbl-6 brbr-6`
          }}
          debounce={500}
          disabled={disableValue}
          name={pairValuePath}
          valueSet={(val) => {
            setFieldValue(pairValuePath, val);
            if (!fieldTouched) {
              setFieldTouched(pairKey, true);
            }
          }}
        />
      </div>
      <div className="flex flex-column w-100 pt2">
        <label className="f7 lh-copy pb1">Error message</label>
        <DebounceInput
          inputValue={errorValue}
          placeholder={"Failed condition error message"}
          customClasses={{
            input: `input--common br-6`
          }}
          debounce={500}
          name={pairErrorPath}
          valueSet={(val) => {
            setFieldValue(pairErrorPath, val);
            if (!fieldTouched) {
              setFieldTouched(pairKey, true);
            }
          }}
        />
      </div>
      {preventDelete ? null : (
        <div className="w-100 w-auto-ns flex justify-end pt2">
          <HoverIconButton
            type="delete"
            tooltip="Delete"
            onClick={() => {
              if (!fieldTouched) {
                setFieldTouched(pairKey, true);
              }
              /**
               * Note: arrayHelpers.remove was resetting the touched value and preventing form submission
               */
              const clonedMetadata = cloneDeep(pairs);

              clonedMetadata.splice(keyIx, 1);
              setFieldValue(name, clonedMetadata);
            }}
          />
        </div>
      )}
    </li>
  );
};

const KeyOperatorValueList = ({ name, emptyMessage }) => {
  const { values } = useFormikContext();
  const pairs = get(values, name);

  if (pairs && pairs.length) {
    return (
      <FieldArray
        name={name}
        render={() => {
          const nodes = pairs.map((pair, keyIx) => (
            <KeyOperatorValueRow
              key={`${name}-${keyIx}`}
              pair={pair}
              keyIx={keyIx}
              name={name}
            />
          ));

          return <ul className="list ma0 pa0">{nodes}</ul>;
        }}
      />
    );
  } else {
    return <Callout copy={emptyMessage || "No key value pairs"} />;
  }
};

export default KeyOperatorValueList;
